import './examOptions.css';
import { optionIsStackedFormula } from './html';

/** Where an answer option's radio button sits beside the option.
 *
 * The row is a flex box of the radio button and the option's text. It used to
 * put both at the top (flex-start), with the button pushed down 4px to meet a
 * line of text. A formula taller than a line of text (a fraction set in
 * display style, a matrix) makes the first line taller and moves its text
 * down, and the button stayed where a line of plain text would have been, at
 * the formula's top. Lined up on the first line's baseline instead, the button
 * sits on that line as it does beside plain text, whatever height the line
 * is: centred on a fraction's bar, and on the first line of an option that
 * runs to several. The button's baseline is the bottom of its box, so it needs
 * no margin of its own above it.
 *
 * An option with no text, only a picture, keeps the top: a picture's baseline
 * is its bottom edge, and the button would sit beside the foot of it. */
export const optionRowAlignment = (hasText) => (hasText ? 'baseline' : 'flex-start');

export const optionRadioStyle = (hasText) => ({ marginTop: hasText ? 0 : 4 });

/** The class that draws an option as a card of its own (examOptions.css). */
export const OPTION_CARD_CLASS = 'exam-option-card';

/** Does this option carry a picture in its image_url? */
export const optionHasPicture = (option) => Boolean(
  option && typeof option.image_url === 'string' && option.image_url.trim(),
);

/** Are a question's options pictures, so that each is drawn as a card?
 *
 * An option with a picture was a radio button and its label on one line and
 * the picture under them, the options 10 px apart down the page. A picture is
 * many lines tall, and the space under each picture, where its expand icon
 * sits, ran straight into the next option's label: TARA-3001-s2 q14's
 * "Diagram A" ... "Diagram E" each read as the caption of the picture above
 * it, not of its own (layout review, 18 October 2026). Every option of such a
 * question is a bordered card instead, the radio button and label at the top
 * of it and the picture directly under them, so a label and its picture are
 * plainly one answer at any width. Asked of the question, not the option, so
 * the cards of one question match. */
export const optionsHavePictures = (options) => Array.isArray(options) && options.some(optionHasPicture);

const PICTURE_OPTION_GAP = 12;
const STACKED_OPTION_EXTRA_GAP = 8;

/** The space between a question's options, in px.
 *
 * `base` for options a line high; a little more between picture cards; and
 * more between options that are a fraction set in display style, two lines
 * tall, so the gap between options is plainly wider than the gaps inside each
 * fraction (optionIsStackedFormula, html.js). */
export const optionListGap = (options, base = 10) => {
  if (!Array.isArray(options)) return base;
  if (optionsHavePictures(options)) return PICTURE_OPTION_GAP;
  if (options.some((option) => option && optionIsStackedFormula(option.text))) return base + STACKED_OPTION_EXTRA_GAP;
  return base;
};
